import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { textColor } from '@ui-kit/styles/colors'

const textBase = tv({
  extend: textColor,
  variants: {
    emphasis: {
      default: '',
      medium: 'opacity-70',
      low: 'opacity-40',
    },
    align: {
      left: 'text-left',
      right: 'text-right',
      center: 'text-center',
    },
    type: {
      primary: 'uppercase',
      secondary: '',
    },
  },
  defaultVariants: { emphasis: 'default', align: 'left', type: 'secondary' },
})

const headingBase = tv({
  extend: textBase,
  variants: {
    type: {
      primary: 'font-adieu-bold',
      secondary: 'font-modern-era-medium',
    },
  },
  defaultVariants: { type: 'secondary' },
})
const regularTextBase = tv({
  extend: textBase,
  base: 'font-modern-era-regular',
  variants: {
    bold: {
      true: 'font-modern-era-medium',
    },
  },
})

export const h1 = tv({
  extend: headingBase,
  base: 'text-5xl leading-14 md:text-8xl lg:text-10xl lg:leading-18',
})

export const h2 = tv({
  extend: headingBase,
  base: 'text-2xl md:text-6xl md:leading-14 lg:text-9xl lg:leading-16',
})

export const h3 = tv({
  extend: textColor,
  base: 'text-xl md:text-2xl md:leading-13 lg:text-4xl',
  variants: {
    emphasis: {
      default: '',
      medium: 'opacity-70',
      low: 'opacity-40',
    },
    align: {
      left: 'text-left',
      right: 'text-right',
      center: 'text-center',
    },
    type: {
      primary: 'font-adieu-bold uppercase md:text-3xl lg:text-6xl',
      secondary: 'font-modern-era-medium',
    },
  },
  defaultVariants: { emphasis: 'default', align: 'left', type: 'secondary' },
})

export const h4 = tv({
  extend: headingBase,
  base: 'text-base md:text-lg md:leading-11 lg:text-xl lg:leading-13',
})

export const h5 = tv({
  extend: textBase,
  base: 'font-modern-era-medium text-sm leading-6 md:text-base md:leading-7 lg:leading-9',
})

export const body1 = tv({
  extend: regularTextBase,
  base: 'text-base md:text-lg md:leading-11 lg:text-xl lg:leading-13',
})

export const body2 = tv({
  extend: regularTextBase,
  base: 'text-sm leading-7 md:text-base lg:leading-8',
})

export const caption = tv({
  extend: regularTextBase,
  base: 'font-modern-era-regular text-xs leading-5 lg:text-sm',
})

export const overline = tv({
  extend: textBase,
  base: 'text-2xs md:text-2xs lg:text-2xs',
})

export const link = 'underline text-brand-dark'

export const buttonLabel = tv({
  extend: regularTextBase,
  base: 'line-clamp-1 font-modern-era-medium text-sm uppercase leading-7',
})
export type BaseTextProps = VariantProps<typeof textBase>
export type RegularTextProps = VariantProps<typeof regularTextBase>
export type HeadingProps = VariantProps<typeof headingBase>
